<template>
  <div class="modal-backdrop" @click.self="handleClose">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="success-icon-wrapper">
            <i class="bi bi-check-circle-fill success-icon"></i>
          </div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <h4 class="success-title">Welcome to FoodCycle!</h4>
          <p class="success-subtitle">Your account has been created successfully</p>
          
          <div class="info-box">
            <i class="bi bi-envelope-check"></i>
            <div class="info-content">
              <h6>Check your email</h6>
              <p>We've sent your login credentials to <strong>{{ email }}</strong></p>
              <p class="small">Please check your spam folder if you don't see the email within a few minutes.</p>
            </div>
          </div>

          <div class="next-steps">
            <h6>What happens next?</h6>
            <ul>
              <li>
                <i class="bi bi-check2"></i>
                <span>Check your email for your password</span>
              </li>
              <li>
                <i class="bi bi-check2"></i>
                <span>Log in with your email and password</span>
              </li>
              <li>
                <i class="bi bi-check2"></i>
                <span>Start exploring sustainable food solutions</span>
              </li>
            </ul>
          </div>

          <div class="countdown-info" v-if="countdown > 0">
            <p>Redirecting to login in <strong>{{ countdown }}</strong> seconds...</p>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="handleLoginRedirect"
          >
            <i class="bi bi-box-arrow-in-right me-2"></i>
            Continue to Login
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';

export default {
  name: 'RegistrationSuccessModal',
  props: {
    email: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const router = useRouter();
    const countdown = ref(10);
    let countdownInterval = null;

    const handleClose = () => {
      emit('close');
    };

    const handleLoginRedirect = () => {
      emit('close');
      router.push('/login');
    };

    onMounted(() => {
      // Start countdown
      countdownInterval = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          clearInterval(countdownInterval);
          handleLoginRedirect();
        }
      }, 1000);
    });

    onUnmounted(() => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    });

    return {
      countdown,
      handleClose,
      handleLoginRedirect
    };
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  animation: fadeIn 0.2s ease;
}

.modal-dialog {
  width: 90%;
  max-width: 500px;
  animation: slideIn 0.3s ease;
}

.modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #87B140, #a8d054);
  padding: 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.modal-header::after {
  content: '';
  position: absolute;
  bottom: -30px;
  left: -10%;
  right: -10%;
  height: 60px;
  background: white;
  border-radius: 50% 50% 0 0;
}

.success-icon-wrapper {
  position: relative;
  z-index: 1;
}

.success-icon {
  font-size: 4rem;
  color: white;
  animation: checkmarkScale 0.5s ease 0.3s both;
}

.modal-body {
  padding: 2rem;
  text-align: center;
}

.success-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.5rem;
  animation: fadeInUp 0.5s ease 0.4s both;
}

.success-subtitle {
  font-size: 1.1rem;
  color: #718096;
  margin-bottom: 2rem;
  animation: fadeInUp 0.5s ease 0.5s both;
}

.info-box {
  background-color: #f7faec;
  border: 2px solid #87B140;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  text-align: left;
  animation: fadeInUp 0.5s ease 0.6s both;
}

.info-box i {
  font-size: 2rem;
  color: #87B140;
  flex-shrink: 0;
}

.info-content h6 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.info-content p {
  margin: 0 0 0.5rem 0;
  color: #4a5568;
}

.info-content p.small {
  font-size: 0.9rem;
  color: #718096;
  margin-bottom: 0;
}

.next-steps {
  text-align: left;
  margin-bottom: 1.5rem;
  animation: fadeInUp 0.5s ease 0.7s both;
}

.next-steps h6 {
  font-size: 1rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 1rem;
}

.next-steps ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.next-steps li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  color: #4a5568;
}

.next-steps i {
  color: #87B140;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.countdown-info {
  background-color: #e6f7d3;
  border-radius: 8px;
  padding: 0.75rem;
  margin-top: 1rem;
  animation: fadeInUp 0.5s ease 0.8s both;
}

.countdown-info p {
  margin: 0;
  color: #2d7e3e;
  font-size: 0.95rem;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: center;
}

.btn {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background: linear-gradient(135deg, #87B140, #a8d054);
  color: white;
  box-shadow: 0 4px 12px rgba(135, 177, 64, 0.3);
}

.btn-primary:hover {
  background: linear-gradient(135deg, #75a236, #96c248);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(135, 177, 64, 0.4);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes checkmarkScale {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 95%;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .success-title {
    font-size: 1.5rem;
  }

  .info-box {
    flex-direction: column;
    text-align: center;
  }

  .next-steps {
    text-align: center;
  }

  .next-steps li {
    justify-content: center;
  }
}
</style>